import React, { memo, useState } from 'react';
import cn from 'classnames';
import { useForm, SubmitHandler } from 'react-hook-form';

import { Icon, Image } from 'components';
import { Row, Col } from 'components/layout';
import { Form, Button, Input, TextArea } from 'components/form';
import ImageUpload from 'components/ImageUpload';
import { CompanyRequest, CompanyResponse } from 'lib/models/company';
import { REGEX } from 'lib/utils/regex';

import styles from './CompanyDetails.module.scss';

export interface CompanyDetailsProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  /** name */
  name: string;
  /** description */
  description: string;
  /** address */
  address: string;
  /** icon_url */
  icon_url: string;
  /** instagram url */
  instagram_url: string;
  /** facebook url */
  facebook_url: string;
  /** linkedin url */
  linkedin_url: string;
  /** website url */
  website: string;
  /** men */
  men: number;
  /** women */
  women: number;
  /** lgbtq */
  lgbtq: number;
  /** on profile update */
  onUpdate: (data: CompanyRequest) => Promise<CompanyResponse | undefined>;
}

export const CompanyDetails: React.FC<CompanyDetailsProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    name,
    description,
    address,
    icon_url,
    instagram_url,
    facebook_url,
    linkedin_url,
    website,
    men,
    women,
    lgbtq,
    onUpdate,
  }: CompanyDetailsProps) => {
    const contentClassNames = cn(styles.CompanyDetails, className);

    const {
      register,
      handleSubmit,
      reset,
      watch,
      formState: { errors, isDirty },
    } = useForm({
      defaultValues: {
        logo: '',
        name,
        description,
        address,
        instagram_url,
        facebook_url,
        linkedin_url,
        website,
        men,
        women,
        lgbtq,
      },
    });

    const [isCompanyUpdate, setIsCompanyUpdate] = useState<boolean>(false);

    const onSubmit: SubmitHandler<CompanyRequest> = async (
      data: CompanyRequest
    ) => {
      setIsCompanyUpdate(true);
      await onUpdate(data);
      setIsCompanyUpdate(false);
      const { logo: _, ...rest } = data;
      reset(rest);
    };

    const imageFiles = watch('logo');
    const image =
      imageFiles?.length > 0 ? URL.createObjectURL(imageFiles[0]) : '';

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid="CompanyDetails"
      >
        <Row className={styles.CompanyDetailsLogo}>
          <Col>
            <ImageUpload
              image={image || icon_url}
              alt={'Company image'}
              {...register('logo')}
            />
          </Col>
        </Row>
        <Form
          autoComplete="off"
          className={styles.CompanyDetailsForm}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Row gutter={50} className={styles.CompanyDetailsFormRow}>
            <Col span={12}>
              <Input
                label="Company Name"
                {...register('name', {
                  required: 'Name is required',
                })}
                isValid={!errors.name}
                helperText={errors.name?.message}
              />
            </Col>
            <Col span={12}>
              <Input
                label="Address"
                {...register('address', {
                  required: 'Address is required',
                })}
                isValid={!errors.address}
                helperText={errors.address?.message}
              />
            </Col>
          </Row>
          <Row className={styles.CompanyDetailsFormRow}>
            <Col span={24}>
              <TextArea
                rows={4}
                label="Company Description"
                {...register('description', {
                  required: 'Description is required',
                })}
                isValid={!errors.description}
                helperText={errors.description?.message}
              />
            </Col>
          </Row>
          <Row className={styles.CompanyDetailsFormRow} gutter={50}>
            <Col span={16}>
              <div className={styles.CompanyDetailsFormSocialLinkContainer}>
                <p className={styles.CompanyDetailsFormSocialLinkTitle}>
                  Social Media Links
                </p>
                <Row gutter={[0, 24]}>
                  <Col
                    span={24}
                    className={styles.CompanyDetailsFormSocialLink}
                  >
                    <Image src="/images/instagram.png" alt="instagram" />
                    <Input
                      className={styles.CompanyDetailsFormSocialLinkInput}
                      {...register('instagram_url', {
                        pattern: {
                          value: REGEX.URL,
                          message: 'Enter a valid url',
                        },
                      })}
                      isValid={!errors.instagram_url}
                      helperText={errors.instagram_url?.message}
                    />
                  </Col>
                  <Col
                    span={24}
                    className={styles.CompanyDetailsFormSocialLink}
                  >
                    <Image src="/images/facebook.png" alt="facebook" />
                    <Input
                      className={styles.CompanyDetailsFormSocialLinkInput}
                      {...register('facebook_url', {
                        pattern: {
                          value: REGEX.URL,
                          message: 'Enter a valid url',
                        },
                      })}
                      isValid={!errors.facebook_url}
                      helperText={errors.facebook_url?.message}
                    />
                  </Col>
                  <Col
                    span={24}
                    className={styles.CompanyDetailsFormSocialLink}
                  >
                    <Image src="/images/linkedin.png" alt="linkedin" />
                    <Input
                      className={styles.CompanyDetailsFormSocialLinkInput}
                      {...register('linkedin_url', {
                        pattern: {
                          value: REGEX.URL,
                          message: 'Enter a valid url',
                        },
                      })}
                      isValid={!errors.linkedin_url}
                      helperText={errors.linkedin_url?.message}
                    />
                  </Col>
                  <Col
                    span={24}
                    className={styles.CompanyDetailsFormSocialLink}
                  >
                    <Image src="/images/web-link.png" alt="web link" />
                    <Input
                      className={styles.CompanyDetailsFormSocialLinkInput}
                      {...register('website', {
                        pattern: {
                          value: REGEX.URL,
                          message: 'Enter a valid url',
                        },
                      })}
                      isValid={!errors.website}
                      helperText={errors.website?.message}
                    />
                  </Col>
                </Row>
              </div>
            </Col>
            <Col span={8}>
              <div className={styles.CompanyDetailsFormGenderRatioContainer}>
                <p className={styles.CompanyDetailsFormGenderRatioTitle}>
                  Gender Ratio
                  <p className={styles.CompanyDetailsFormDesc}>
                    What&apos;s the Gender ratio of your current workforce?
                  </p>
                </p>
                <Row
                  align="stretch"
                  className={styles.CompanyDetailsFormGenderRatio}
                >
                  <Col lg={8} align="center">
                    <p>Men</p>
                  </Col>
                  <Col lg={10}>
                    <Input
                      type="number"
                      className={styles.CompanyDetailsFormGenderRatioInput}
                      {...register('men', {
                        pattern: {
                          value: REGEX.NUMBER,
                          message: 'Enter a valid number',
                        },
                      })}
                      isValid={!errors.men}
                      helperText={errors.men?.message}
                    />
                  </Col>
                  <Col
                    lg={6}
                    className={styles.CompanyDetailsFormGenderRatioPercentage}
                  >
                    <Icon iconName="percentage" />
                  </Col>
                </Row>
                <Row
                  align="stretch"
                  className={styles.CompanyDetailsFormGenderRatio}
                >
                  <Col lg={8} align="center">
                    <p>Women</p>
                  </Col>
                  <Col lg={10}>
                    <Input
                      type="number"
                      className={styles.CompanyDetailsFormGenderRatioInput}
                      {...register('women', {
                        pattern: {
                          value: REGEX.NUMBER,
                          message: 'Enter a valid number',
                        },
                      })}
                      isValid={!errors.women}
                      helperText={errors.women?.message}
                    />
                  </Col>
                  <Col
                    lg={6}
                    className={styles.CompanyDetailsFormGenderRatioPercentage}
                  >
                    <Icon iconName="percentage" />
                  </Col>
                </Row>
                <Row
                  align="stretch"
                  className={styles.CompanyDetailsFormGenderRatio}
                >
                  <Col lg={8} align="center">
                    <p>LGBTQ</p>
                  </Col>
                  <Col lg={10}>
                    <Input
                      {...register('lgbtq', {
                        pattern: {
                          value: REGEX.NUMBER,
                          message: 'Enter a valid number',
                        },
                      })}
                      isValid={!errors.lgbtq}
                      helperText={errors.lgbtq?.message}
                      type="number"
                      className={styles.CompanyDetailsFormGenderRatioInput}
                    />
                  </Col>
                  <Col
                    lg={6}
                    className={styles.CompanyDetailsFormGenderRatioPercentage}
                  >
                    <Icon iconName="percentage" />
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
          <Button
            className={styles.CompanyDetailsFormSubmit}
            type="submit"
            color="primary"
            variant="contained"
            size="large"
            disabled={!isDirty || isCompanyUpdate}
            isLoading={isCompanyUpdate}
          >
            Save Changes
          </Button>
        </Form>
      </div>
    );
  }
);

CompanyDetails.displayName = 'CompanyDetails';
